import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { Outlet } from 'react-router';
import * as selectors from '../../store/selectors';
import * as dateHelper from '../../helpers/dates';
import * as rampHelper from '../../helpers/ramp-helper';
import { useEventCallback } from '../../helpers/hooks';
import {
  getStatistics,
  setStatisticsFilters,
  clearStatisticsFilters,
} from '../../store/statistics.actions';
import OutboundIcon from '@mui/icons-material/FlightTakeoff';
import InboundIcon from '@mui/icons-material/FlightLand';
import { Toolbar, Divider, Tab, Tabs } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Filters from './Filters';
import OutboundStatsTable from './OutboundStatsTable';
import InboundStatsTable from './InboundStatsTable';
import FullScreenDialog from '../FullScreenDialog';

const useStyles = makeStyles(theme => ({
  filtersToolbar: {
    padding: 0,
    flex: '0 0 auto',
    overflowY: 'auto',
  },
  content: {
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  tablesWrapper: {
    display: 'flex',
    flex: '1 0 0',
  },
  tabs: {
    flex: '0 0 auto',
    alignSelf: 'flex-end',
  },
  tab: {
    minHeight: 'auto',
  },
  tabLabelIcon: {
    marginRight: theme.spacing(1),
  },
  tabWrapper: {
    flexDirection: 'row',
  },
}));

const StatsDialog = ({
  airports,
  events,
  filteredEvents,
  rampServices,
  filters,
  isOpen,
  getStatistics,
  setStatisticsFilters,
  clearStatisticsFilters,
  onClose,
}) => {
  const [range, setRange] = useState(dateHelper.dateToMonthRange());
  const [tab, setTab] = useState(1);
  const classes = useStyles();

  const inboundAirports = useMemo(
    () => Array.from(new Set(events.map(event => event.inboundAirport))).sort(),
    [events]
  );

  const outboundAirports = useMemo(
    () =>
      Array.from(new Set(events.map(event => event.outboundAirport))).sort(),
    [events]
  );

  const airlines = useMemo(
    () => Array.from(new Set(events.map(event => event.airline))).sort(),
    [events]
  );

  const sortedRampServices = useMemo(
    () => [
      ...rampHelper.getFixedRampServices(rampServices),
      ...rampHelper.getNotFixedRampServices(rampServices),
    ],
    [rampServices]
  );

  const handleInboundAirportsChange = useEventCallback(
    airports => {
      setStatisticsFilters({ inboundAirports: airports });
    },
    [setStatisticsFilters]
  );

  const handleOutboundAirportsChange = useEventCallback(
    airports => {
      setStatisticsFilters({ outboundAirports: airports });
    },
    [setStatisticsFilters]
  );

  useEffect(() => {
    getStatistics(range);
  }, [getStatistics, range]);

  const handleYearMonthChange = useEventCallback(date => {
    setRange(dateHelper.dateToMonthRange(date));
    handleInboundAirportsChange([]);
    handleOutboundAirportsChange([]);
  });

  const handleAirlinesChange = useEventCallback(
    airlines => {
      setStatisticsFilters({ airlines });
    },
    [setStatisticsFilters]
  );

  const handleAirportsChange = useEventCallback(
    airports => {
      setStatisticsFilters({ airports });
    },
    [setStatisticsFilters]
  );

  const handleFiltersClear = useEventCallback(
    () => clearStatisticsFilters(),
    [clearStatisticsFilters]
  );

  const handleTabChange = useEventCallback((event, index) => setTab(index), []);

  return (
    <FullScreenDialog
      isOpen={isOpen}
      contentClassName={classes.content}
      title="Statistics"
      onClose={onClose}
    >
      <Toolbar className={classes.filtersToolbar} variant="dense">
        <Tabs
          className={classes.tabs}
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={handleTabChange}
        >
          <Tab
            className={classes.tab}
            classes={{
              icon: classes.tabLabelIcon,
              wrapped: classes.tabWrapper,
            }}
            icon={<InboundIcon />}
            label="Arrival"
            wrapped={true}
          />
          <Tab
            className={classes.tab}
            classes={{
              icon: classes.tabLabelIcon,
              wrapped: classes.tabWrapper,
            }}
            icon={<OutboundIcon />}
            label="Departure"
            wrapped={true}
          />
        </Tabs>
        <Divider orientation="vertical" />
        <Filters
          airlines={airlines}
          airports={airports}
          inboundAirports={inboundAirports}
          outboundAirports={outboundAirports}
          filters={filters}
          onAirlinesChange={handleAirlinesChange}
          onAirportsChange={handleAirportsChange}
          onInboundAirportsChange={handleInboundAirportsChange}
          onOutboundAirportsChange={handleOutboundAirportsChange}
          onYearMonthChange={handleYearMonthChange}
          onFiltersClear={handleFiltersClear}
        />
      </Toolbar>
      <Divider />
      <div className={classes.tablesWrapper}>
        {tab === 0 ? (
          <InboundStatsTable events={filteredEvents} />
        ) : (
          <OutboundStatsTable
            events={filteredEvents}
            rampServices={sortedRampServices}
          />
        )}
      </div>
      <Outlet />
    </FullScreenDialog>
  );
};

const mapDispatchToProps = {
  getStatistics,
  setStatisticsFilters,
  clearStatisticsFilters,
};

export default connect(
  state => ({
    airports: selectors.getAirports(state),
    events: selectors.getStatistics(state),
    filteredEvents: selectors.getFilteredStatistics(state),
    filters: selectors.getStatisticsFilters(state),
    rampServices: selectors.getSortedRampServices(state),
  }),
  mapDispatchToProps
)(StatsDialog);
